import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskTitle, Reveal, SectionLabel } from "./motion";
import { DRIVER, SPECS } from "@/lib/content";
import { useLang } from "@/context/LangContext";

export const Bio = () => {
  const { t, lang } = useLang();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="bio" data-testid="bio-section" className="relative bg-ink px-5 py-24 md:px-10 md:py-36">
      <div className="mx-auto max-w-[1600px]">
        <SectionLabel index="01">{t.bio.label}</SectionLabel>
        <MaskTitle key={lang} lines={[t.bio.title[0], { text: t.bio.title[1], className: "text-volt" }]}
          className="mt-6 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl lg:text-9xl" />

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-5">
          <Reveal className="md:col-span-5 md:row-span-2">
            <div ref={imgRef} className="corner-clip group relative h-[440px] overflow-hidden bg-surface md:h-full md:min-h-[560px]">
              <motion.img style={{ y }} src="/images/grid.jpg" alt="Lucas Pasquinetti #77 Trident"
                className="absolute inset-0 h-[116%] w-full -translate-y-[8%] object-cover object-[60%_50%] transition-transform duration-[1.2s] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <p className="font-display text-3xl font-black uppercase leading-none">{DRIVER.firstName}<br />{DRIVER.lastName}</p>
                <p className="font-display text-7xl font-black leading-none text-volt">77</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-7">
            <div className="h-full border border-white/10 bg-surface p-6 md:p-10">
              <p className="label-mono text-volt">{t.bio.bio}</p>
              {t.bio.p.map((p, i) => (
                <p key={i} className={`mt-5 max-w-2xl leading-relaxed ${i === 0 ? "text-lg text-white md:text-2xl" : "text-sm text-zinc-400 md:text-base"}`} data-testid={`bio-paragraph-${i}`}>{p}</p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="md:col-span-7">
            <div className="flex h-full min-h-[220px] items-end justify-between gap-6 bg-trident p-6 text-white md:p-8" data-testid="bio-number-card">
              <div>
                <p className="label-mono">{t.bio.race_number}</p>
                <p className="label-mono mt-3 text-white/80">{DRIVER.team} · {DRIVER.championshipShort}</p>
              </div>
              <p className="font-display text-[8rem] font-black leading-[0.75] md:text-[11rem]">77</p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-12">
            <dl className="grid grid-cols-2 border border-white/10 md:grid-cols-3" data-testid="bio-specs">
              {SPECS.map((sp, i) => (
                <div key={sp.key} className={`border-white/10 p-5 md:p-6 ${i % 2 ? "" : "border-r"} ${i < 4 ? "border-b" : ""} ${(i + 1) % 3 ? "md:border-r" : "md:border-r-0"} ${i < 3 ? "md:border-b" : "md:border-b-0"}`}>
                  <dt className="label-mono text-white">{t.bio.specs[sp.key]}</dt>
                  <dd className="mt-2 font-display text-xl font-extrabold uppercase text-white md:text-2xl">{sp.v == null ? t.bio.tbd : typeof sp.v === "object" ? sp.v[lang] : sp.v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
